import React, { useEffect, useState } from 'react';
import CircleButton, { getCircleButtonClass, } from 'components/v2/buttons/CircleButton';
import { DocumentCheckIcon } from '@heroicons/react/24/solid';
import CheckIcon from 'components/v2/icons/CheckIcon';
import CargomaticTooltip from 'components/v2/Tooltip';
import { useTranslation } from 'react-i18next';
import StopCompleteModal from 'components/modules/shipments/stops/StopCompleteModal';
import SessionStore from 'stores/SessionStore';
import { getYards } from 'utils/YardAPIUtils';
import ShipmentsActionCreators from 'actions/ShipmentsActionCreators';
import ShipmentWebAPIUtils, { handleStopEquipmentUpdateFail, handleStopEquipmentUpdateSuccess, } from 'utils/ShipmentWebAPIUtils';
import OutgateConfirmationModal from 'components/modules/shipments/OutgateConfirmationModal';
import ConfirmModal from 'components/ui/modals/ConfirmModal';
import { isDrayageShipment, isIntermodalShipmentType, } from 'helpers/shipment-types';
import { getShipperStopDocumentRequirementsFromConfig, getUniqueCombinedStopDocumentRequirementsFromConfig, } from 'helpers/stops';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
const initConfirmationModalDetails = {
    type: 'outgate',
    title: 'ADDITIONAL_STOP_CONFIRM_TITLE',
    message: 'ADDITIONAL_STOP_CONFIRM_MESSAGE',
    confirmButtonText: 'YES',
    hideButtonText: 'NO',
};
export default function StopCompleteNextStep({ item, stops, shipment }) {
    var _a;
    const { t } = useTranslation();
    const [showModal, setShowModal] = useState(false);
    const [yards, setYards] = useState(null);
    const [confirmationModalDetails, setConfirmationModalDetails] = useState(initConfirmationModalDetails);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [showOutgateConfirmationModal, setShowOutgateConfirmationModal] = useState(false);
    const [shipperStopDocumentRequirements, setShipperStopDocumentRequirements] = useState({});
    const isCarrier = SessionStore.isCarrier();
    useEffect(() => {
        const getYardsData = async () => {
            var _a, _b;
            const { market: { market_id: marketId }, } = shipment;
            const yardsResponse = await getYards('nameInternal', 'ascend', null, null, {
                id: null,
                marketId,
                nameInternal: null,
                status: null,
            });
            setYards((_b = (_a = yardsResponse === null || yardsResponse === void 0 ? void 0 : yardsResponse.data) === null || _a === void 0 ? void 0 : _a.getYards) === null || _b === void 0 ? void 0 : _b.yards);
        };
        const getShipperStopDocRequirements = async () => {
            var _a;
            if (isIntermodalShipmentType) {
                const shipperId = ((_a = shipment === null || shipment === void 0 ? void 0 : shipment.shipper) === null || _a === void 0 ? void 0 : _a._id) || (shipment === null || shipment === void 0 ? void 0 : shipment.shipper);
                const isCarrierMandatoryStopDocumentsEnabled = featureEnabled(CONFIGURED_FEATURES.CARRIER_MANDATORY_STOP_DOCUMENTS);
                const stopDocReqs = isCarrierMandatoryStopDocumentsEnabled
                    ? await getUniqueCombinedStopDocumentRequirementsFromConfig(shipperId)
                    : await getShipperStopDocumentRequirementsFromConfig(shipperId);
                setShipperStopDocumentRequirements(stopDocReqs);
            }
        };
        try {
            getYardsData();
            getShipperStopDocRequirements();
        }
        catch (e) {
            console.error('Error Loading Next Steps', e);
            setShipperStopDocumentRequirements({});
        }
    }, [shipment]);
    const createAction = () => {
        const stop = stops.find((s) => { var _a; return s._id === ((_a = item === null || item === void 0 ? void 0 : item.meta) === null || _a === void 0 ? void 0 : _a.stop); });
        const offsite = (stop === null || stop === void 0 ? void 0 : stop.off_site) ? '_OFFSITE' : '';
        const empty = (stop === null || stop === void 0 ? void 0 : stop.empty) ? '_EMPTY' : '';
        const action = (stop === null || stop === void 0 ? void 0 : stop.action) === 'deliver' ? '_DELIVERY' : '_PICKUP';
        return `COMPLETE${offsite}${empty}${action}`;
    };
    const renderIcon = () => {
        return !item.complete ? (React.createElement(DocumentCheckIcon, { type: "solid", className: "w-5 h-5" })) : (React.createElement("div", { "data-testid": "div-d98ab4f0", className: getCircleButtonClass({ size: 'small', color: 'brand' }) },
            React.createElement(CheckIcon, null)));
    };
    const handleStopCompletion = (shipment, currentStop, file, meta) => {
        const { shipper: { prompt_for_offsite, tags }, } = shipment;
        const promptForOutgate = prompt_for_offsite || (tags || []).includes('prompt_for_offsite');
        ShipmentsActionCreators.completeStop(shipment, currentStop, file, meta);
        if (promptForOutgate) {
            handleOutgateConfirmationDetails(currentStop);
        }
    };
    const handleOutgateConfirmationDetails = (stop) => {
        if (isDrayageShipment(shipment) && stop.action === 'pickup') {
            setConfirmationModalDetails(Object.assign(Object.assign({}, confirmationModalDetails), { stop }));
            setShowConfirmationModal(true);
        }
    };
    const handleOutgateConfirmation = () => {
        if (confirmationModalDetails.type === 'outgate') {
            setShowConfirmationModal(false);
            setShowOutgateConfirmationModal(true);
        }
    };
    const handleOutgateStopCreation = async (payload) => {
        setShowOutgateConfirmationModal(false);
        await ShipmentWebAPIUtils.createOutgateShipmentStops(shipment._id, payload);
    };
    const renderCompleteButton = (item, stops) => {
        if (!(item && stops)) {
            return null;
        }
        const circleButton = (shouldDisable) => (React.createElement(CircleButton, { dataTestId: "CircleButton-e48d9b8a", size: "small", disabled: shouldDisable, onClick: () => setShowModal(true) }, renderIcon()));
        if (item.complete) {
            return circleButton(true);
        }
        const disabled = !!item.disabled;
        if (disabled) {
            return ((React.createElement(CargomaticTooltip, { dataTestId: "CargomaticTooltip-c21c5cc7", position: "top", body: () => circleButton(disabled) }, t('COMPLETE_PREVIOUS_STEPS'))));
        }
        return circleButton(false);
    };
    return ((React.createElement("div", { "data-testid": "div-37d824a6" },
        React.createElement("div", { "data-testid": "div-67ad8093", className: "flex justify-between text-sm text-gray-700  py-1 items-center" },
            React.createElement("span", { "data-testid": "span-75bb09f0" }, t(createAction())),
            renderCompleteButton(item, stops)),
        showModal && (React.createElement(StopCompleteModal, { show: showModal, shipmentId: shipment._id, stopId: (_a = item === null || item === void 0 ? void 0 : item.meta) === null || _a === void 0 ? void 0 : _a.stop, onStopComplete: handleStopCompletion, onStopEquipmentUpdateSuccess: handleStopEquipmentUpdateSuccess, onStopEquipmentUpdateFail: handleStopEquipmentUpdateFail, onHide: () => setShowModal(false), isCarrier: isCarrier, yards: yards, shipperStopDocumentRequirements: shipperStopDocumentRequirements })),
        showConfirmationModal && (React.createElement(ConfirmModal, { dataTestId: "ConfirmModal-fc330225", show: showConfirmationModal, title: t(confirmationModalDetails.title), message: t(confirmationModalDetails.message), affirmative: t(confirmationModalDetails.confirmButtonText), hideButtonText: t(confirmationModalDetails.hideButtonText), onConfirm: handleOutgateConfirmation, onHide: () => setShowConfirmationModal(false) })),
        showOutgateConfirmationModal && (React.createElement(OutgateConfirmationModal, { show: showOutgateConfirmationModal, onCancel: () => setShowOutgateConfirmationModal(false), onSave: handleOutgateStopCreation, stop: confirmationModalDetails.stop })))));
}
